import { useEffect, useRef, useState } from "react";
import {
  Card, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Loader2, Link2, MessageCircle, Copy, Download, Trash2, Eye,
  FileText, ClipboardCheck, GraduationCap, RefreshCw, Pencil, Save,
} from "lucide-react";
import api, { downloadBlob, filenameFromHeader, errorMessage } from "@/lib/api";
import { CONTACT, buildWhatsAppLink } from "@/lib/contact";

const STATUS_STYLES = {
  submitted: "bg-sky-100 text-ark-skyDeep",
  reviewed: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};
const STATUS_OPTS = ["submitted", "reviewed", "approved", "rejected"];

const PROGRAM_LABELS = {
  playgroup: "Play Group", nursery: "Nursery", lkg: "LKG", ukg: "UKG", daycare: "Day Care",
};

const shareUrlFor = (token) => `${window.location.origin}/admission/${token}`;

const AdmissionsPanel = ({ isSuper = false }) => {
  const [tab, setTab] = useState("submissions"); // 'submissions' | 'links'
  const [links, setLinks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null); // full admission

  const inFlight = useRef(false);
  const pollingStopped = useRef(false);

  const loadAll = async (silent = false) => {
    if (inFlight.current || pollingStopped.current) return;
    inFlight.current = true;
    if (!silent) setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/admission-links"),
        api.get("/admin/admissions"),
      ]);
      setLinks(l.data); setSubs(s.data);
    } catch (err) {
      // Token expired mid-poll: stop the interval so we don't keep hammering.
      if (err?.response?.status === 401) pollingStopped.current = true;
      if (!silent) toast.error(await errorMessage(err, "Failed to load admissions."));
    } finally {
      inFlight.current = false;
      if (!silent) setLoading(false);
    }
  };

  // Refresh on mount, when the browser tab regains focus, and every 15s in the
  // background so a fresh submission appears without a manual reload.
  useEffect(() => {
    let cancelled = false;
    loadAll();
    const onFocus = () => !cancelled && loadAll(true);
    const onVisible = () => !document.hidden && !cancelled && loadAll(true);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => !cancelled && loadAll(true), 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createLink = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/admission-links");
      const url = shareUrlFor(data.token);
      const msg = `Hi! 👋 Please fill your child's admission form for ${CONTACT.schoolName}:\n\n${url}\n\nThe link is valid for 7 days and can be filled once. Reach out to us on ${CONTACT.phone} if you need any help. — Little Ark Preschool`;
      // Open WhatsApp with pre-filled message so coordinator can pick the parent's chat.
      window.open(buildWhatsAppLink(msg), "_blank", "noopener,noreferrer");
      toast.success("Admission link created — WhatsApp opened.");
      setLinks((cur) => [{ ...data, url }, ...cur]);
    } catch (err) {
      toast.error(await errorMessage(err, "Could not create link."));
    } finally { setCreating(false); }
  };

  const copyLink = async (token) => {
    const url = shareUrlFor(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed, please copy manually: " + url);
    }
  };

  const shareOnWhatsApp = (token) => {
    const url = shareUrlFor(token);
    const msg = `Hi! Please fill your child's admission form for ${CONTACT.schoolName}: ${url}`;
    window.open(buildWhatsAppLink(msg), "_blank", "noopener,noreferrer");
  };

  const deleteLink = async (id) => {
    try {
      await api.delete(`/admin/admission-links/${id}`);
      setLinks((cur) => cur.filter((x) => x.id !== id));
      toast.success("Link deleted.");
    } catch (err) { toast.error(await errorMessage(err, "Could not delete.")); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/admissions/${id}`);
      setDetail(data);
    } catch (err) { toast.error(await errorMessage(err, "Could not load details.")); }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/admissions/${id}`, { status });
      setSubs((cur) => cur.map((x) => (x.id === id ? { ...x, status: data.status } : x)));
      if (detail?.id === id) setDetail(data);
      toast.success("Status updated.");
    } catch (err) { toast.error(await errorMessage(err, "Could not update.")); }
  };

  const removeSub = async (id) => {
    try {
      await api.delete(`/admin/admissions/${id}`);
      setSubs((cur) => cur.filter((x) => x.id !== id));
      if (detail?.id === id) setDetail(null);
      toast.success("Admission deleted.");
    } catch (err) { toast.error(await errorMessage(err, "Could not delete.")); }
  };

  // ---------- Edit (superadmin only) ----------
  const [edit, setEdit] = useState(null); // null | { id, form }
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (row) => setEdit({ id: row.id, form: { ...row } });
  const setField = (k, v) => setEdit((e) => (e ? { ...e, form: { ...e.form, [k]: v } } : e));

  const saveEdit = async () => {
    if (!edit) return;
    setSavingEdit(true);
    try {
      // Only send whitelisted fields the backend accepts.
      const editable = [
        "status", "admission_no", "academic_year", "date_of_admission", "admission_seeking",
        "student_first_name", "student_middle_name", "student_last_name", "date_of_birth",
        "gender", "blood_group", "aadhaar", "nationality", "mother_tongue", "other_languages",
        "religion", "category",
        "house_no", "street", "area", "landmark", "village_city", "taluk", "district", "state", "pin_code",
        "father_name", "father_qualification", "father_occupation", "father_company",
        "father_mobile", "father_whatsapp", "father_email",
        "mother_name", "mother_qualification", "mother_occupation", "mother_company",
        "mother_mobile", "mother_whatsapp", "mother_email",
        "special_needs", "allergies", "doctor_name", "medical_conditions",
        "hospital", "current_medication", "emergency_contact_number",
        "prev_school", "prev_class", "prev_reason",
        "pickup_types", "pickup_other", "authorized_person_name", "authorized_relationship",
        "authorized_phone", "authorized_address",
        "emergency_name", "emergency_relationship", "emergency_phone", "emergency_alt_phone",
        "documents_submitted",
      ];
      const payload = {};
      editable.forEach((k) => {
        const v = edit.form[k];
        if (v !== undefined && v !== null && v !== "") payload[k] = v;
      });
      const { data } = await api.patch(`/admin/admissions/${edit.id}`, payload);
      setSubs((cur) => cur.map((x) => (x.id === edit.id ? { ...x, ...data } : x)));
      if (detail?.id === edit.id) setDetail(data);
      toast.success("Admission updated.");
      setEdit(null);
    } catch (err) { toast.error(await errorMessage(err, "Could not save changes.")); }
    finally { setSavingEdit(false); }
  };

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(`/admin/admissions/${row.id}/pdf`, { responseType: "blob" });
      const first = (row.student_first_name || "student").toLowerCase();
      const last = (row.student_last_name || "").toLowerCase();
      const fname = filenameFromHeader(res.headers, `admission_${first}_${last}.pdf`.replace(/_+/g, "_"));
      downloadBlob(res.data, fname);
    } catch (err) { toast.error(await errorMessage(err, "Could not download PDF.")); }
  };

  const filteredSubs = subs.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${s.student_first_name || ""} ${s.student_last_name || ""}`.toLowerCase();
    return (
      name.includes(q) ||
      (s.father_mobile || "").includes(q) ||
      (s.mother_mobile || "").includes(q) ||
      (s.father_name || "").toLowerCase().includes(q) ||
      (s.mother_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="admissions-panel">
      {/* Top actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setTab("submissions")} data-testid="admissions-tab-submissions"
            aria-pressed={tab === "submissions"}
            className={`text-sm font-semibold rounded-full px-4 py-2 border transition ${
              tab === "submissions"
                ? "bg-ark-sky text-white border-ark-sky"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            <ClipboardCheck className="h-4 w-4 inline mr-1.5" /> Submissions
          </button>
          <button onClick={() => setTab("links")} data-testid="admissions-tab-links"
            aria-pressed={tab === "links"}
            className={`text-sm font-semibold rounded-full px-4 py-2 border transition ${
              tab === "links"
                ? "bg-ark-sky text-white border-ark-sky"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            <Link2 className="h-4 w-4 inline mr-1.5" /> Shared Links
          </button>
        </div>
        <div className="flex gap-2">
          {tab === "submissions" && (
            <Input placeholder="Search name / phone…" value={search}
              data-testid="admissions-search"
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-2xl h-11 border-slate-200 w-full md:w-64" />
          )}
          <Button onClick={() => loadAll()} disabled={loading} data-testid="admissions-refresh-btn"
            variant="outline" className="rounded-full h-11 px-4 border-slate-200 shrink-0" title="Refresh submissions">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button onClick={createLink} disabled={creating} data-testid="admissions-create-link"
            className="rounded-full h-11 px-5 bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold shrink-0">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageCircle className="h-4 w-4 mr-2" /> Share on WhatsApp</>}
          </Button>
        </div>
      </div>

      {/* Content */}
      <Card className="border-0 rounded-3xl shadow-ark-soft">
        <CardContent className="p-6">
          {loading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : tab === "submissions" ? (
            filteredSubs.length === 0 ? (
              <div className="py-10 text-center text-slate-500" data-testid="admissions-empty">
                No admission forms submitted yet. Tap “Share on WhatsApp” to send a link to a parent.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSubs.map((s) => (
                  <div key={s.id} data-testid={`admission-row-${s.id}`}
                    className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-ark-sky to-ark-teal flex items-center justify-center text-white font-display font-bold shrink-0">
                      {(s.student_first_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ark-ink truncate">
                        {[s.student_first_name, s.student_middle_name, s.student_last_name].filter(Boolean).join(" ") || "—"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {s.father_mobile || s.mother_mobile || "—"} · Parents: {s.father_name || "—"} / {s.mother_name || "—"}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Submitted {(s.submitted_at || "").slice(0, 10)} · via {s.coordinator_name || s.coordinator_email || "coordinator"}
                      </div>
                    </div>
                    <Badge variant="outline" className="rounded-full font-semibold border-slate-200 text-slate-600 shrink-0">
                      <GraduationCap className="h-3 w-3 mr-1" /> {PROGRAM_LABELS[s.admission_seeking] || s.admission_seeking}
                    </Badge>
                    {isSuper ? (
                      <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                        <SelectTrigger data-testid={`admission-status-${s.id}`}
                          className={`w-32 h-8 text-xs font-semibold rounded-full border-0 ${STATUS_STYLES[s.status] || ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTS.map((o) => (
                            <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`capitalize rounded-full border-0 font-semibold ${STATUS_STYLES[s.status] || ""}`}>
                        {s.status}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1">
                      <button onClick={() => openDetail(s.id)} data-testid={`admission-view-${s.id}`}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-white text-slate-500"
                        title="View" aria-label="View admission details">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => downloadPdf(s)} data-testid={`admission-download-${s.id}`}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-sky-50 text-ark-skyDeep"
                        title="Download A4 PDF" aria-label="Download A4 PDF">
                        <Download className="h-4 w-4" />
                      </button>
                      {isSuper && (
                        <button onClick={() => openEdit(s)} data-testid={`admission-edit-${s.id}`}
                          className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-amber-50 text-amber-600"
                          title="Edit admission" aria-label="Edit admission">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {isSuper && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`admission-delete-${s.id}`}
                              className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-red-500"
                              title="Delete" aria-label="Delete admission">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this admission?</AlertDialogTitle>
                              <AlertDialogDescription>
                                The record for {s.student_first_name} {s.student_last_name} will be permanently removed.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeSub(s.id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Links list
            links.length === 0 ? (
              <div className="py-10 text-center text-slate-500" data-testid="admission-links-empty">
                No admission links created yet.
              </div>
            ) : (
              <div className="space-y-3">
                {links.map((l) => {
                  const url = shareUrlFor(l.token);
                  const expired = l.expires_at && new Date(l.expires_at) < new Date();
                  const status = l.used ? "used" : expired ? "expired" : "active";
                  const statusCls = {
                    active: "bg-emerald-100 text-emerald-700",
                    used: "bg-slate-100 text-slate-500",
                    expired: "bg-red-100 text-red-600",
                  }[status];
                  return (
                    <div key={l.id} data-testid={`link-row-${l.id}`}
                      className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ark-ink truncate">{url}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Created {(l.created_at || "").slice(0, 10)} by {l.created_by_name || l.created_by} · Expires {(l.expires_at || "").slice(0, 10)}
                        </div>
                      </div>
                      <Badge className={`capitalize rounded-full border-0 font-semibold ${statusCls} shrink-0`}>{status}</Badge>
                      <div className="flex items-center gap-1">
                        <button onClick={() => copyLink(l.token)} data-testid={`link-copy-${l.id}`}
                          className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-white text-slate-500"
                          title="Copy link" aria-label="Copy link">
                          <Copy className="h-4 w-4" />
                        </button>
                        <button onClick={() => shareOnWhatsApp(l.token)} data-testid={`link-share-${l.id}`}
                          className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-green-50 text-green-600"
                          title="Share on WhatsApp" aria-label="Share on WhatsApp">
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        {isSuper && (
                          <button onClick={() => deleteLink(l.id)} data-testid={`link-delete-${l.id}`}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-red-500"
                            title="Delete" aria-label="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="admission-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-ark-ink">
              {detail && [detail.student_first_name, detail.student_middle_name, detail.student_last_name].filter(Boolean).join(" ")}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <Section title="Admission Details">
                <Kv l="Admission No." v={detail.admission_no} />
                <Kv l="Academic Year" v={detail.academic_year} />
                <Kv l="Date of Admission" v={detail.date_of_admission} />
                <Kv l="Seeking For" v={PROGRAM_LABELS[detail.admission_seeking]} />
              </Section>
              <Section title="Student">
                <Kv l="Date of Birth" v={detail.date_of_birth} />
                <Kv l="Gender" v={detail.gender} cap />
                <Kv l="Blood Group" v={detail.blood_group} />
                <Kv l="Aadhaar" v={detail.aadhaar} />
                <Kv l="Nationality" v={detail.nationality} />
                <Kv l="Mother Tongue" v={detail.mother_tongue} />
                <Kv l="Religion" v={detail.religion} />
                <Kv l="Other Languages" v={detail.other_languages} />
                <Kv l="Category" v={detail.category} />
                {detail.student_photo && (
                  <div className="col-span-full">
                    <div className="text-xs font-semibold text-slate-500">Student Photo</div>
                    <img src={detail.student_photo} alt="Student" className="mt-1 h-32 w-24 object-cover rounded-xl border border-slate-200" />
                  </div>
                )}
              </Section>
              <Section title="Address">
                <Kv l="House No." v={detail.house_no} />
                <Kv l="Street" v={detail.street} />
                <Kv l="Area" v={detail.area} />
                <Kv l="Landmark" v={detail.landmark} />
                <Kv l="Village / City" v={detail.village_city} />
                <Kv l="Taluk" v={detail.taluk} />
                <Kv l="District" v={detail.district} />
                <Kv l="State" v={detail.state} />
                <Kv l="PIN Code" v={detail.pin_code} />
              </Section>
              <Section title="Father">
                <Kv l="Name" v={detail.father_name} />
                <Kv l="Qualification" v={detail.father_qualification} />
                <Kv l="Occupation" v={detail.father_occupation} />
                <Kv l="Company" v={detail.father_company} />
                <Kv l="Mobile" v={detail.father_mobile} />
                <Kv l="WhatsApp" v={detail.father_whatsapp} />
                <Kv l="Email" v={detail.father_email} />
              </Section>
              <Section title="Mother">
                <Kv l="Name" v={detail.mother_name} />
                <Kv l="Qualification" v={detail.mother_qualification} />
                <Kv l="Occupation" v={detail.mother_occupation} />
                <Kv l="Company" v={detail.mother_company} />
                <Kv l="Mobile" v={detail.mother_mobile} />
                <Kv l="WhatsApp" v={detail.mother_whatsapp} />
                <Kv l="Email" v={detail.mother_email} />
              </Section>
              <Section title="Medical">
                <Kv l="Special Needs" v={detail.special_needs} />
                <Kv l="Allergies" v={detail.allergies} />
                <Kv l="Medical Conditions" v={detail.medical_conditions} />
                <Kv l="Current Medication" v={detail.current_medication} />
                <Kv l="Doctor" v={detail.doctor_name} />
                <Kv l="Hospital" v={detail.hospital} />
                <Kv l="Emergency No." v={detail.emergency_contact_number} />
              </Section>
              <Section title="Previous School">
                <Kv l="School Name" v={detail.prev_school} />
                <Kv l="Class Studied" v={detail.prev_class} />
                <Kv l="Reason for Leaving" v={detail.prev_reason} />
              </Section>
              <Section title="Pickup & Emergency">
                <Kv l="Pickup Types" v={(detail.pickup_types || []).join(", ")} />
                <Kv l="Pickup Other" v={detail.pickup_other} />
                <Kv l="Authorized Person" v={detail.authorized_person_name} />
                <Kv l="Relationship" v={detail.authorized_relationship} />
                <Kv l="Phone" v={detail.authorized_phone} />
                <Kv l="Emergency Name" v={detail.emergency_name} />
                <Kv l="Emergency Phone" v={detail.emergency_phone} />
                <Kv l="Alt. Phone" v={detail.emergency_alt_phone} />
              </Section>
              <Section title="Documents Submitted">
                <Kv l="Docs" v={(detail.documents_submitted || []).join(", ")} />
                <Kv l="Declaration Agreed" v={detail.declaration_agreed ? "Yes" : "No"} />
              </Section>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => detail && downloadPdf(detail)} data-testid="admission-detail-pdf"
              className="rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
              <Download className="h-4 w-4 mr-2" /> Download A4 PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit modal (super admin only) */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="admission-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-ark-ink flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-600" /> Edit admission
            </DialogTitle>
          </DialogHeader>
          {edit && <EditForm form={edit.form} setField={setField} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-full">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="admission-edit-save"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-display font-semibold">
              {savingEdit ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Edit form (flat, one long list of inputs) ----------
const FIELD_GROUPS = [
  { title: "Admission", fields: [["admission_no","Admission No."],["academic_year","Academic Year"],["date_of_admission","Date of Admission"],["admission_seeking","Seeking For (playgroup|nursery|lkg|ukg|daycare)"]] },
  { title: "Student", fields: [["student_first_name","First Name"],["student_middle_name","Middle Name"],["student_last_name","Last Name"],["date_of_birth","Date of Birth (YYYY-MM-DD)"],["gender","Gender (male|female)"],["blood_group","Blood Group"],["aadhaar","Aadhaar"],["nationality","Nationality"],["mother_tongue","Mother Tongue"],["other_languages","Other Languages"],["religion","Religion"],["category","Category"]] },
  { title: "Address", fields: [["house_no","House No."],["street","Street"],["area","Area"],["landmark","Landmark"],["village_city","Village / City"],["taluk","Taluk"],["district","District"],["state","State"],["pin_code","PIN Code"]] },
  { title: "Father", fields: [["father_name","Name"],["father_qualification","Qualification"],["father_occupation","Occupation"],["father_company","Company"],["father_mobile","Mobile"],["father_whatsapp","WhatsApp"],["father_email","Email"]] },
  { title: "Mother", fields: [["mother_name","Name"],["mother_qualification","Qualification"],["mother_occupation","Occupation"],["mother_company","Company"],["mother_mobile","Mobile"],["mother_whatsapp","WhatsApp"],["mother_email","Email"]] },
  { title: "Medical", fields: [["special_needs","Special Needs"],["allergies","Allergies"],["medical_conditions","Medical Conditions"],["current_medication","Current Medication"],["doctor_name","Doctor"],["hospital","Hospital"],["emergency_contact_number","Emergency Number"]] },
  { title: "Previous School", fields: [["prev_school","School"],["prev_class","Class"],["prev_reason","Reason for leaving"]] },
  { title: "Pickup & Emergency", fields: [["pickup_other","Pickup — other note"],["authorized_person_name","Authorized Person"],["authorized_relationship","Relationship"],["authorized_phone","Phone"],["authorized_address","Address"],["emergency_name","Emergency Name"],["emergency_relationship","Emergency Relationship"],["emergency_phone","Emergency Phone"],["emergency_alt_phone","Alternate Phone"]] },
];

const EditForm = ({ form, setField }) => (
  <div className="space-y-5 text-sm">
    <div>
      <label className="text-xs font-semibold text-slate-500">Status</label>
      <Select value={form.status || "submitted"} onValueChange={(v) => setField("status", v)}>
        <SelectTrigger data-testid="edit-status" className="mt-1 h-10 rounded-2xl border-slate-200">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    {FIELD_GROUPS.map((g) => (
      <div key={g.title}>
        <h4 className="text-xs font-bold uppercase tracking-wider text-ark-skyDeep mb-2">{g.title}</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          {g.fields.map(([k, label]) => (
            <div key={k}>
              <label htmlFor={`edit-${k}`} className="text-xs font-semibold text-slate-500">{label}</label>
              <Input id={`edit-${k}`} data-testid={`edit-${k}`}
                value={form[k] || ""}
                onChange={(e) => setField(k, e.target.value)}
                className="mt-1 h-10 rounded-2xl border-slate-200" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-bold uppercase tracking-wider text-ark-skyDeep mb-2">{title}</h4>
    <div className="grid sm:grid-cols-3 gap-2 rounded-2xl bg-slate-50 p-4">{children}</div>
  </div>
);
const Kv = ({ l, v, cap }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-semibold text-slate-500">{l}</div>
    <div className={`text-slate-800 truncate ${cap ? "capitalize" : ""}`}>{v || "—"}</div>
  </div>
);

export default AdmissionsPanel;
